import { useState, useEffect, useRef } from 'react';
import { useListFilterContext } from 'react-admin';
import { TextField, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ClearIcon from '@material-ui/icons/Clear';

const useStyles = makeStyles({
  root: {
    marginTop: '8px',
    marginBottom: '8px',
    minWidth: '220px',
  },
});

interface DebouncedTextInputProps {
  label: string;
  source: string;
  debounceTime?: number;
  alwaysOn?: boolean;
  resettable?: boolean;
  'data-cy'?: string;
  [key: string]: any;
}

export const DebouncedTextInput = ({
  label,
  source,
  debounceTime = 1200,
  resettable,
  ...props
}: DebouncedTextInputProps) => {
  const classes = useStyles();
  const { setFilters, filterValues, displayedFilters } = useListFilterContext();
  const [inputValue, setInputValue] = useState(filterValues[source] || '');
  const isFirstRender = useRef(true);

  console.log('[DebouncedTextInput] Render:', {
    source,
    inputValue,
    filterValue: filterValues[source],
    debounceTime,
  });

  // Sync with external filter changes (e.g., reset button)
  useEffect(() => {
    const externalValue = filterValues[source] || '';
    console.log('[DebouncedTextInput] External filter sync:', {
      source,
      externalValue,
      currentInputValue: inputValue,
    });
    if (externalValue !== inputValue) {
      setInputValue(externalValue);
    }
  }, [filterValues[source]]);

  // Debounce effect
  useEffect(() => {
    console.log('[DebouncedTextInput] Debounce effect triggered:', {
      source,
      inputValue,
      isFirstRender: isFirstRender.current,
      debounceTime,
      timestamp: new Date().toISOString(),
    });

    // Skip debounce on first render to avoid initial unnecessary request
    if (isFirstRender.current) {
      isFirstRender.current = false;
      console.log('[DebouncedTextInput] Skipping first render');
      return;
    }

    console.log(`[DebouncedTextInput] Setting timeout for ${debounceTime}ms`);
    const handler = setTimeout(() => {
      console.log('[DebouncedTextInput] Timeout fired! Calling setFilters:', {
        source,
        inputValue,
        timestamp: new Date().toISOString(),
      });
      setFilters(
        { ...filterValues, [source]: inputValue },
        displayedFilters,
        false,
      );
    }, debounceTime);

    return () => {
      console.log('[DebouncedTextInput] Clearing timeout');
      clearTimeout(handler);
    };
  }, [inputValue]);

  const handleChange = (e: any) => {
    const newValue = e.target.value;
    console.log('[DebouncedTextInput] Input changed:', {
      source,
      newValue,
      timestamp: new Date().toISOString(),
    });
    setInputValue(newValue);
  };

  const handleClear = () => {
    console.log('[DebouncedTextInput] Clearing input');
    setInputValue('');
  };

  return (
    <TextField
      {...props}
      className={classes.root}
      label={label}
      value={inputValue}
      onChange={handleChange}
      variant="filled"
      margin="dense"
      InputProps={{
        endAdornment: resettable && inputValue ? (
          <IconButton size="small" onClick={handleClear}>
            <ClearIcon fontSize="small" />
          </IconButton>
        ) : null,
      }}
    />
  );
};
